import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
} from 'framer-motion'
import { glareBackground } from './cardTilt'
import ZoomStage from './ZoomStage'
const EASE = [0.16, 1, 0.3, 1]

const TOP_BAR_HEIGHT = 56
const STAGE_PADDING_TOP = `calc(${TOP_BAR_HEIGHT}px + env(safe-area-inset-top))`
const STAGE_PADDING_BOTTOM = 'calc(96px + env(safe-area-inset-bottom))'
const IMAGE_MAX_HEIGHT =
  'calc(100dvh - 152px - env(safe-area-inset-top) - env(safe-area-inset-bottom))'
const IMAGE_MAX_WIDTH = 'calc(100vw - 16px)'
const BADGE_LIFT_PX = 80
const IMMERSIVE_PADDING_BOTTOM = 'calc(56px + env(safe-area-inset-bottom))'
const IMMERSIVE_IMAGE_MAX_HEIGHT =
  'calc(100dvh - 112px - env(safe-area-inset-top) - env(safe-area-inset-bottom))'
const ROUND_BUTTON =
  'pointer-events-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 active:bg-white/25'
function pad(value) {
  return String(value).padStart(2, '0')
}
function MediaLightbox({
  media,
  onClose,
  onPrev,
  onNext,
  position,
  badge,
  legend,
  immersive = false,
  tilt = false,
}) {
  const closeRef = useRef(null)
  const directionRef = useRef(0)
  const open = media !== null
  const goPrev = useMemo(
    () =>
      onPrev
        ? () => {
            directionRef.current = -1
            onPrev()
          }
        : void 0,
    [onPrev]
  )
  const goNext = useMemo(
    () =>
      onNext
        ? () => {
            directionRef.current = 1
            onNext()
          }
        : void 0,
    [onNext]
  )
  const handleKey = useCallback(
    (event) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowLeft') goPrev?.()
      if (event.key === 'ArrowRight') goNext?.()
    },
    [onClose, goPrev, goNext]
  )
  useEffect(() => {
    if (!open) return
    window.addEventListener('keydown', handleKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus({ preventScroll: true })
    return () => {
      window.removeEventListener('keydown', handleKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, handleKey])
  useEffect(() => {
    if (!open) directionRef.current = 0
  }, [open])
  // Portaled to <body>: some callers sit inside transformed ancestors, which would turn
  // `position: fixed` into a box relative to that ancestor instead of the viewport.
  return createPortal(
    <AnimatePresence>
      {media && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          role="dialog"
          aria-modal="true"
          aria-label={media.kind === 'image' ? media.alt : 'Video player'}
          className={`fixed inset-x-0 top-0 z-[60] ${immersive && media.kind === 'image' ? 'bg-black/55 backdrop-blur-2xl backdrop-saturate-150' : 'bg-black'}`}
          style={{ height: '100dvh' }}
        >
          {media.kind === 'image' ? (
            // Keyed so paging to another frame starts from a clean zoom, gateway
            // fallback and loading state.
            <ImageViewer
              key={media.url}
              url={media.url}
              alt={media.alt}
              backUrl={media.backUrl}
              caption={media.caption}
              direction={directionRef.current}
              badge={badge}
              legend={legend}
              immersive={immersive}
              tiltEnabled={tilt}
              onClose={onClose}
              onPrev={goPrev}
              onNext={goNext}
            />
          ) : (
            <VideoViewer url={media.url} caption={media.caption} onClose={onClose} />
          )}

          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between px-3"
            style={{
              paddingTop: 'env(safe-area-inset-top)',
              height: `calc(${TOP_BAR_HEIGHT}px + env(safe-area-inset-top))`,
            }}
          >
            <span
              className="pl-2 font-m1-mono text-[14px] tabular-nums text-white/80"
              aria-live="polite"
            >
              {position ? `${pad(position.index + 1)} / ${pad(position.total)}` : ''}
            </span>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={ROUND_BUTTON}
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>

          {/* Arrows are for mouse users; on a touch screen the swipe replaces them. */}
          {goPrev && (
            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous"
              className={`${ROUND_BUTTON} absolute left-6 top-1/2 z-10 hidden -translate-y-1/2 [@media(pointer:fine)]:flex`}
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
          )}
          {goNext && (
            <button
              type="button"
              onClick={goNext}
              aria-label="Next"
              className={`${ROUND_BUTTON} absolute right-6 top-1/2 z-10 hidden -translate-y-1/2 [@media(pointer:fine)]:flex`}
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
function VideoViewer({ url, caption, onClose }) {
  return (
    <>
      <div
        className="absolute inset-0 flex items-center justify-center px-3"
        style={{ paddingTop: STAGE_PADDING_TOP, paddingBottom: STAGE_PADDING_BOTTOM }}
        onClick={onClose}
      >
        <motion.video
          key={url}
          src={url}
          controls
          autoPlay
          playsInline
          initial={{ scale: 0.97, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3, ease: EASE }}
          onClick={(event) => event.stopPropagation()}
          className="w-full max-w-5xl bg-black"
          style={{ maxHeight: IMAGE_MAX_HEIGHT }}
        />
      </div>
      {caption && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center px-4"
          style={{ paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}
        >
          <p className="text-[14px] tracking-[0.03em] text-white/80">{caption}</p>
        </div>
      )}
    </>
  )
}
function ImageViewer({
  url,
  alt,
  backUrl,
  caption,
  direction,
  badge,
  legend,
  immersive,
  tiltEnabled,
  onClose,
  onPrev,
  onNext,
}) {
  const tiltX = useMotionValue(0)
  const tiltY = useMotionValue(0)
  const tilt = useMemo(
    () => (tiltEnabled ? { x: tiltX, y: tiltY } : void 0),
    [tiltEnabled, tiltX, tiltY]
  )
  const glare = useTransform([tiltX, tiltY], ([rx, ry]) => glareBackground(rx, ry))
  const [ratio, setRatio] = useState(null)
  const turn = useMotionValue(0)
  // The seal belongs to the front: it fades out as soon as the card starts to turn.
  const badgeOpacity = useTransform(turn, [-40, 0, 40], [0, 1, 0])
  const [flipped, setFlipped] = useState(false)
  const [backFailed, setBackFailed] = useState(false)
  const reduceMotion = useReducedMotion()
  const canFlip = Boolean(backUrl) && !backFailed
  const src = url
  const toggleFlip = useCallback(() => {
    const angle = turn.get()
    const showingBack = Math.abs(Math.round(angle / 180)) % 2 === 1
    // Turn in the same direction as the last drag when possible, else forward.
    const next = showingBack
      ? Math.round(angle / 180) * 180 - (angle > 0 ? 180 : -180)
      : Math.round(angle / 180) * 180 + 180
    animate(turn, next, { duration: reduceMotion ? 0.01 : 0.7, ease: EASE })
  }, [reduceMotion, turn])
  useMotionValueEvent(turn, 'change', (value) => {
    setFlipped(Math.abs(Math.round(value / 180)) % 2 === 1)
  })

  // "F" turns the card over for keyboard users.
  useEffect(() => {
    if (!canFlip) return undefined
    const onKey = (event) => {
      if (event.key === 'f' || event.key === 'F') toggleFlip()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canFlip, toggleFlip])
  const measure = (image) => {
    if (image.naturalWidth > 0 && image.naturalHeight > 0) {
      setRatio(image.naturalWidth / image.naturalHeight)
    }
  }
  const boxStyle = {
    position: 'relative',
    width:
      ratio === null
        ? 0
        : `min(${IMAGE_MAX_WIDTH}, calc(${immersive ? IMMERSIVE_IMAGE_MAX_HEIGHT : IMAGE_MAX_HEIGHT} * ${ratio}))`,
    aspectRatio: ratio ?? void 0,
  }
  return (
    <>
      <motion.div
        initial={{ opacity: 0, x: direction * 48 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.35, ease: EASE }}
        className="absolute inset-0"
      >
        <ZoomStage
          onClose={onClose}
          onPrev={onPrev}
          onNext={onNext}
          tilt={tilt}
          turn={turn}
          canTurn={canFlip}
          paddingTop={STAGE_PADDING_TOP}
          paddingBottom={immersive ? IMMERSIVE_PADDING_BOTTOM : STAGE_PADDING_BOTTOM}
        >
          {(zoom) => (
            <div style={boxStyle}>
              {/* Only this layer leans; the legend below stays upright and readable. */}
              <motion.div
                className="absolute inset-0"
                style={{
                  rotateX: tiltX,
                  rotateY: tiltY,
                  transformPerspective: 900,
                  transformStyle: 'preserve-3d',
                }}
              >
                <motion.div
                  className="absolute inset-0"
                  style={{ transformStyle: 'preserve-3d', rotateY: turn }}
                >
                  <img
                    src={src}
                    alt={alt}
                    draggable={false}
                    decoding="async"
                    referrerPolicy="no-referrer"
                    className="absolute inset-0 h-full w-full select-none object-contain"
                    style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                    onLoad={(event) => measure(event.currentTarget)}
                  />
                  {canFlip && (
                    <img
                      src={backUrl}
                      alt={`${alt} (back)`}
                      draggable={false}
                      decoding="async"
                      referrerPolicy="no-referrer"
                      className="absolute inset-0 h-full w-full select-none object-contain"
                      style={{
                        transform: 'rotateY(180deg)',
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                      }}
                      onError={() => setBackFailed(true)}
                    />
                  )}
                </motion.div>
                {tilt && (
                  <motion.div
                    className="pointer-events-none absolute inset-0"
                    style={{ background: glare }}
                    aria-hidden="true"
                  />
                )}
                {badge && (
                  <ZoomFade scale={zoom} fade={badgeOpacity}>
                    {badge}
                  </ZoomFade>
                )}
              </motion.div>
              {legend && (
                <ZoomFade scale={zoom} placement="below">
                  {legend}
                </ZoomFade>
              )}
            </div>
          )}
        </ZoomStage>
      </motion.div>

      {canFlip && (
        <button
          type="button"
          onClick={toggleFlip}
          aria-pressed={flipped}
          aria-label={flipped ? 'Show the front of the card' : 'Show the back of the card'}
          className="pointer-events-auto absolute bottom-5 right-5 z-10 flex h-12 items-center gap-2 rounded-full bg-white/10 px-5 text-sm font-medium text-white transition-colors hover:bg-white/20 active:bg-white/25"
          style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
        >
          <i className="ri-refresh-line text-xl" aria-hidden="true" />
          {flipped ? 'Show front' : 'Show back'}
        </button>
      )}

      {!immersive && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col items-center gap-3 px-4"
          style={{ paddingBottom: 'calc(20px + env(safe-area-inset-bottom))' }}
        >
          {caption && <p className="text-[14px] tracking-[0.03em] text-white/80">{caption}</p>}
        </div>
      )}
    </>
  )
}
function OriginalOnZoom({ scale, onZoom }) {
  useMotionValueEvent(scale, 'change', (value) => {
    if (value > ORIGINAL_ZOOM_THRESHOLD) onZoom()
  })
  return null
}
function ZoomFade({ scale, children, placement = 'corner', fade }) {
  const opacity = useTransform(fade ? [scale, fade] : [scale], ([zoom, extra = 1]) => {
    const zoomOpacity = Math.min(1, Math.max(0, 1 - (zoom - 1) / 0.2))
    return zoomOpacity * extra
  })
  return (
    <motion.div
      style={placement === 'corner' ? { opacity, z: BADGE_LIFT_PX } : { opacity }}
      className={
        placement === 'corner'
          ? 'pointer-events-none absolute right-2 top-2 z-10'
          : 'pointer-events-none absolute inset-x-0 top-full z-10 mt-3.5 flex justify-center'
      }
    >
      {children}
    </motion.div>
  )
}
export { MediaLightbox as default }
